import { useEffect, useState } from "react";
import {
  completeMfaLogin,
  getAccountSessions,
  getOrCreateAuthSession,
  isMfaLoginChallenge,
  loginPasswordAccount,
  logoutOtherAccountSessions,
  logoutPasswordAccount,
  registerPasswordAccount
} from "../../api/auth-client";
import type { AccountSession, AuthSessionResponse } from "../../api/auth.types";
import {
  addUltraMember,
  clearActiveUltraOrganizationId,
  createUltraOrganization,
  getActiveUltraOrganizationId,
  getUltraEntitlements,
  getUltraUsage,
  getUltraWorkspace,
  listUltraOrganizations,
  setActiveUltraOrganizationId,
  setUltraBudget,
  type UltraEntitlements,
  type UltraSeatType,
  type UltraUsageSummary,
  type UltraWorkspace,
  type UltraWorkspaceSummary
} from "../../api/ultra-client";
import type { OmnisyniaClientModule } from "../module.types";

function UltraWorkspaceModule() {
  const [session, setSession] = useState<AuthSessionResponse>();
  const [entitlements, setEntitlements] = useState<UltraEntitlements>();
  const [organizations, setOrganizations] = useState<UltraWorkspaceSummary[]>([]);
  const [workspace, setWorkspace] = useState<UltraWorkspace>();
  const [usage, setUsage] = useState<UltraUsageSummary>();
  const [accountSessions, setAccountSessions] = useState<readonly AccountSession[]>([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [mfaChallengeToken, setMfaChallengeToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [organizationName, setOrganizationName] = useState("Mi empresa");
  const [budget, setBudget] = useState(10_000);
  const [memberEmail, setMemberEmail] = useState("");
  const [memberSeat, setMemberSeat] = useState<UltraSeatType>("member");
  const [partnerEmail, setPartnerEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  useEffect(() => {
    void (async () => {
      try {
        const [activeSession, catalog] = await Promise.all([getOrCreateAuthSession(), getUltraEntitlements()]);
        setSession(activeSession);
        setEntitlements(catalog);
        if (activeSession.user.mode === "account") {
          await Promise.all([loadOrganizations(activeSession), loadAccountSecurity(activeSession)]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "No se pudo cargar Ultra.");
      }
    })();
  }, []);

  async function loadOrganizations(activeSession: AuthSessionResponse) {
    const items = await listUltraOrganizations(activeSession.sessionToken);
    setOrganizations(items);
    const preferredId = getActiveUltraOrganizationId();
    const selected = items.find((item) => item.organization.id === preferredId) ?? items[0];
    if (selected) await openWorkspace(activeSession, selected.organization.id);
  }

  async function loadAccountSecurity(activeSession: AuthSessionResponse) {
    setAccountSessions(await getAccountSessions(activeSession.sessionToken));
  }

  async function openWorkspace(activeSession: AuthSessionResponse, organizationId: string) {
    const [nextWorkspace, nextUsage] = await Promise.all([
      getUltraWorkspace(activeSession.sessionToken, organizationId),
      getUltraUsage(activeSession.sessionToken, organizationId)
    ]);
    setActiveUltraOrganizationId(organizationId);
    setWorkspace(nextWorkspace);
    setUsage(nextUsage);
    setBudget(nextWorkspace.organization.monthlyCreditLimit);
  }

  async function authenticate(action: "register" | "login") {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const nextSession = action === "register"
        ? await registerPasswordAccount({ email, password, displayName })
        : await loginPasswordAccount({ email, password });
      if (isMfaLoginChallenge(nextSession)) {
        setMfaChallengeToken(nextSession.challengeToken);
        setNotice("Introduce el código de tu aplicación Authenticator para terminar el acceso.");
        return;
      }
      setSession(nextSession);
      await Promise.all([loadOrganizations(nextSession), loadAccountSecurity(nextSession)]);
      setNotice(action === "register" ? "Cuenta creada. Ya puedes crear tu espacio Ultra." : "Sesión iniciada.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo completar el acceso.");
    } finally {
      setBusy(false);
    }
  }

  async function createWorkspace() {
    if (!session) return;
    setBusy(true);
    setError(undefined);
    try {
      const created = await createUltraOrganization(session.sessionToken, {
        name: organizationName,
        monthlyCreditLimit: budget
      });
      setActiveUltraOrganizationId(created.organization.id);
      setWorkspace(created);
      setUsage(await getUltraUsage(session.sessionToken, created.organization.id));
      setOrganizations(await listUltraOrganizations(session.sessionToken));
      setNotice("Espacio OMNISYNIA Ultra creado y activado.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear el espacio Ultra.");
    } finally {
      setBusy(false);
    }
  }

  async function saveBudget() {
    if (!session || !workspace) return;
    setBusy(true);
    setError(undefined);
    try {
      await setUltraBudget(session.sessionToken, workspace.organization.id, budget);
      await openWorkspace(session, workspace.organization.id);
      setNotice("Límite mensual actualizado.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo actualizar el límite.");
    } finally {
      setBusy(false);
    }
  }

  async function addMember() {
    if (!session || !workspace) return;
    setBusy(true);
    setError(undefined);
    try {
      await addUltraMember(session.sessionToken, workspace.organization.id, {
        email: memberEmail,
        seat: memberSeat
      });
      await openWorkspace(session, workspace.organization.id);
      setMemberEmail("");
      setNotice("Miembro añadido al espacio Ultra.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo añadir el miembro.");
    } finally {
      setBusy(false);
    }
  }

  async function completeSecondFactor() {
    setBusy(true);
    setError(undefined);
    try {
      const nextSession = await completeMfaLogin(mfaChallengeToken, mfaCode);
      setSession(nextSession);
      setMfaChallengeToken("");
      setMfaCode("");
      await Promise.all([loadOrganizations(nextSession), loadAccountSecurity(nextSession)]);
      setNotice("Identidad confirmada. Sesión iniciada de forma segura.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo comprobar el código.");
    } finally {
      setBusy(false);
    }
  }

  async function switchAccount() {
    if (!session) return;
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await logoutPasswordAccount(session.sessionToken);
    } finally {
      clearActiveUltraOrganizationId();
      setSession(undefined);
      setOrganizations([]);
      setWorkspace(undefined);
      setUsage(undefined);
      setAccountSessions([]);
      setEmail("");
      setPassword("");
      setDisplayName("");
      setNotice("Sesión cerrada. Ya puedes crear o abrir la cuenta personal del segundo socio.");
      setBusy(false);
    }
  }

  async function addFounderPartner() {
    if (!session || !workspace) return;
    setBusy(true);
    setError(undefined);
    try {
      await addUltraMember(session.sessionToken, workspace.organization.id, {
        email: partnerEmail,
        seat: "admin",
        founderRole: "creator_partner"
      });
      await openWorkspace(session, workspace.organization.id);
      setPartnerEmail("");
      setNotice("La cuenta personal del segundo socio ha quedado vinculada como Ultra Admin.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo vincular la cuenta del socio.");
    } finally {
      setBusy(false);
    }
  }

  async function closeOtherSessions() {
    if (!session) return;
    setBusy(true);
    setError(undefined);
    try {
      const revoked = await logoutOtherAccountSessions(session.sessionToken);
      await loadAccountSecurity(session);
      setNotice(revoked > 0 ? `${revoked} sesión(es) anterior(es) cerrada(s).` : "No había otras sesiones abiertas.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudieron cerrar las otras sesiones.");
    } finally {
      setBusy(false);
    }
  }

  const usagePercent = usage ? Math.min(100, (usage.creditsUsed / usage.monthlyCreditLimit) * 100) : 0;
  const isAccount = session?.user.mode === "account";
  const creator = workspace?.members.find((member) => member.founderRole === "creator");
  const creatorPartner = workspace?.members.find((member) => member.founderRole === "creator_partner");
  const isUnlimitedFounder = Boolean(workspace?.membership.founderRole);

  return (
    <section className="editor-shell ultra-shell" aria-labelledby="ultra-title">
      <header className="editor-header">
        <div>
          <p className="eyebrow">OMNISYNIA BUSINESS</p>
          <h2 id="ultra-title">OMNISYNIA Ultra</h2>
          <p>Empresas, puestos, permisos y consumo administrados desde un solo lugar.</p>
        </div>
        <div className="ultra-header-actions">
          <span className="ultra-badge">ULTRA</span>
          {isAccount && (
            <button className="secondary-button" disabled={busy} onClick={() => void switchAccount()} type="button">
              Cambiar cuenta
            </button>
          )}
        </div>
      </header>

      {!isAccount ? (
        <div className="ultra-access-card">
          <div>
            <h3>Activa tu espacio empresarial</h3>
            <p>Necesitas una cuenta registrada. Tu historial invitado no se comparte con la empresa.</p>
          </div>
          {mfaChallengeToken ? (
            <>
              <div className="ultra-form-grid">
                <label>Código de 6 cifras o código de respaldo<input autoComplete="one-time-code" inputMode="numeric" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} /></label>
              </div>
              <div className="ultra-actions">
                <button className="primary-button" disabled={busy || !mfaCode} onClick={() => void completeSecondFactor()} type="button">Confirmar identidad</button>
                <button className="secondary-button" disabled={busy} onClick={() => { setMfaChallengeToken(""); setMfaCode(""); }} type="button">Volver</button>
              </div>
            </>
          ) : (
            <>
              <div className="ultra-form-grid">
                <label>Nombre<input autoComplete="name" maxLength={100} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
                <label>Correo<input autoComplete="email" maxLength={320} type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
                <label>Contraseña<input autoComplete="current-password" maxLength={128} minLength={12} type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
              </div>
              <p className="ultra-helper">Para cuentas nuevas: 12 caracteres y al menos tres tipos entre mayúsculas, minúsculas, números y símbolos.</p>
              <div className="ultra-actions">
                <button className="primary-button" disabled={busy || !email || password.length < 12} onClick={() => void authenticate("register")} type="button">Crear cuenta</button>
                <button className="secondary-button" disabled={busy || !email || !password} onClick={() => void authenticate("login")} type="button">Ya tengo cuenta</button>
              </div>
            </>
          )}
        </div>
      ) : organizations.length === 0 && !workspace ? (
        <div className="ultra-access-card">
          <h3>Crea tu primer espacio Ultra</h3>
          <div className="ultra-form-grid">
            <label>Nombre de la empresa<input value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} /></label>
            <label>Límite mensual de créditos<input type="number" min={1} value={budget} onChange={(event) => setBudget(Number(event.target.value))} /></label>
          </div>
          <button className="primary-button" disabled={busy || organizationName.trim().length < 2} onClick={() => void createWorkspace()} type="button">Crear espacio Ultra</button>
        </div>
      ) : workspace && usage ? (
        <div className="ultra-dashboard">
          <div className="ultra-workspace-bar">
            <div>
              <span className="eyebrow">ESPACIO ACTIVO</span>
              <h3>{workspace.organization.name}</h3>
            </div>
            {organizations.length > 1 && (
              <select value={workspace.organization.id} onChange={(event) => session && void openWorkspace(session, event.target.value)}>
                {organizations.map((item) => <option key={item.organization.id} value={item.organization.id}>{item.organization.name}</option>)}
              </select>
            )}
          </div>

          <div className="ultra-stat-grid">
            <article><span>Puesto</span><strong>{workspace.membership.seat}</strong></article>
            <article><span>Miembros</span><strong>{workspace.members.length}</strong></article>
            <article><span>Usados</span><strong>{usage.creditsUsed}</strong></article>
            <article><span>Disponibles</span><strong>{isUnlimitedFounder ? "Sin límite" : usage.creditsRemaining}</strong></article>
          </div>

          <div className="ultra-founder-card">
            <span className="eyebrow">DOS CUENTAS PERSONALES</span>
            <h3>Socios fundadores</h3>
            <div className="ultra-founder-grid">
              <article>
                <span>Creador</span>
                <strong>{creator?.displayName ?? "Cuenta creadora"}</strong>
                <small>{creator?.email}</small>
                <em>Ultra · Sin límites</em>
              </article>
              <article>
                <span>Socio del creador</span>
                <strong>{creatorPartner?.displayName ?? "Cuenta pendiente"}</strong>
                <small>{creatorPartner?.email ?? "El segundo socio debe crear su cuenta personal"}</small>
                <em>{creatorPartner ? "Ultra · Sin límites" : "Pendiente de vincular"}</em>
              </article>
            </div>
            {!creatorPartner && workspace.permissions.includes("members.manage") && (
              <div className="ultra-inline-form ultra-partner-form">
                <label>Correo de la cuenta personal del socio<input type="email" value={partnerEmail} onChange={(event) => setPartnerEmail(event.target.value)} /></label>
                <button className="secondary-button" disabled={busy || !partnerEmail} onClick={() => void addFounderPartner()} type="button">Vincular segundo socio</button>
              </div>
            )}
            <p className="ultra-helper">Los dos socios conservan cuentas separadas y acceso Ultra Admin. No deben compartir contraseña.</p>
            <p className="ultra-helper">Los roles fundadores están protegidos: la cuenta creadora permanece como creador y el segundo fundador como socio del creador.</p>
          </div>

          <div className="ultra-security-card">
            <div>
              <span className="eyebrow">SEGURIDAD DE LA CUENTA</span>
              <h3>Sesiones protegidas</h3>
              <p>{accountSessions.length} sesión(es) activa(s). La sesión actual seguirá abierta.</p>
            </div>
            <div className="ultra-session-list">
              {accountSessions.slice(0, 4).map((item) => (
                <p key={item.id}>
                  <span>{item.current ? "Este dispositivo" : "Otro acceso"}</span>
                  <small>Último uso: {new Date(item.lastSeenAt).toLocaleString("es-ES")}</small>
                </p>
              ))}
            </div>
            <button className="secondary-button" disabled={busy || accountSessions.length <= 1} onClick={() => void closeOtherSessions()} type="button">
              Cerrar otras sesiones
            </button>
          </div>

          <div className="ultra-budget-card">
            <div className="ultra-progress"><span style={{ width: `${usagePercent}%` }} /></div>
            <p>{usagePercent.toFixed(0)} % del presupuesto mensual del resto del equipo utilizado. Las dos cuentas fundadoras no tienen este límite.</p>
            {workspace.permissions.includes("billing.manage") && (
              <div className="ultra-inline-form">
                <label>Límite mensual<input type="number" min={1} value={budget} onChange={(event) => setBudget(Number(event.target.value))} /></label>
                <button className="secondary-button" disabled={busy} onClick={() => void saveBudget()} type="button">Guardar límite</button>
              </div>
            )}
          </div>

          {workspace.permissions.includes("members.manage") && (
            <div className="ultra-member-card">
              <h3>Equipo</h3>
              <div className="ultra-member-list">
                {workspace.members.map((member) => <p key={member.userId}><span>{member.displayName ?? member.email ?? member.userId.slice(0, 8)}</span><strong>{member.founderRole === "creator" ? "Creador" : member.founderRole === "creator_partner" ? "Socio · Admin" : member.seat}</strong></p>)}
              </div>
              <div className="ultra-inline-form">
                <label>Correo de una cuenta existente<input type="email" value={memberEmail} onChange={(event) => setMemberEmail(event.target.value)} /></label>
                <label>Puesto<select value={memberSeat} onChange={(event) => setMemberSeat(event.target.value as UltraSeatType)}>{entitlements?.seats.map((seat) => <option key={seat.id} value={seat.id}>{seat.name}</option>)}</select></label>
                <button className="secondary-button" disabled={busy || !memberEmail} onClick={() => void addMember()} type="button">Añadir</button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className="ultra-seat-grid">
        {entitlements?.seats.map((seat) => (
          <article key={seat.id}>
            <span>{seat.name}</span>
            <p>{seat.description}</p>
            <small>{seat.permissions.length} permisos incluidos</small>
          </article>
        ))}
      </div>

      {notice && <p className="success-message">{notice}</p>}
      {error && <p className="error-message">{error}</p>}
    </section>
  );
}

export const ultraClientModule: OmnisyniaClientModule = {
  id: "omnisynia-ultra",
  displayName: "OMNISYNIA Ultra",
  description: "Espacios empresariales, puestos, permisos y consumo.",
  tier: "premium",
  status: "available",
  Component: UltraWorkspaceModule
};
